import { Suspense, useCallback, useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import type { DisconnectReason } from '@amzn/work-spaces-agentic-web-viewer';
import {
  useConnectionSettingsStore,
  WebInSessionError,
} from '@amzn/work-spaces-web-client-core-lib';
import { CoreFactory } from '@/bridge/factory/CoreFactory';
import ConnectStatus from '@/components/ConnectStatus';
import { useReportHeartbeatTask } from '@/hooks/useReportHeartbeatTask';
import type { HeartbeatErrorControls } from '@/hooks/useReportHeartbeatTask';
import { useSessionStore } from '@/stores/session';
import { useLogUploaderPreferenceStore } from '@stores/loguploader';
import { isAgenticWorkspace } from '@views/Stream/agentic';
import { handleHeartbeatError } from '@views/Stream/heartbeatError';
import { buildAgenticDcvObservability } from './agenticDcvObservability';
import { createAgenticHostMetrics } from './agenticHostMetrics';
import {
  AGENTIC_SAML_HEARTBEAT_OPERATION,
  SAML_HEARTBEAT_ERROR_METRIC,
} from './agenticMetricNames';
import { LazyAgenticViewer } from './agenticViewerChunk';
import AgenticRouteErrorBoundary from './AgenticRouteErrorBoundary';
import {
  hasWspContextHandoff,
  removeWspContextHandoff,
} from './wspContextHandoff';

// The viewer logs through the client's logger, so its lines reach the upload.
const logger = CoreFactory.getLogger();
const metrics = createAgenticHostMetrics(CoreFactory.getMetrics(), logger);

// Where scripts/copy-dcv-assets.sh serves the DCV SDK's workers, as for Maxibon.
const DCV_ASSETS_PATH = '/static/dcvjs/';

// The viewer's own page uses this, so it pads for the phone's safe areas. Not
// Maxibon's viewport: its overlays-content mode lets the keyboard cover the chat.
const AGENTIC_VIEWPORT =
  'width=device-width, initial-scale=1.0, viewport-fit=cover';

// DCV Web SDK closing-reason codes that core-lib does not name.
const DISCONNECTION_REQUESTED_CODE = 14;
const END_SESSION_BY_USER_CODE = 60;

// The clean ends wsp.html shows no alert for. Only ClosingReason, because a
// connection error 14 is GATEWAY_BUSY and keeps its alert.
const CLOSING_REASONS_WITHOUT_ERROR = new Set([
  Number(WebInSessionError.WspClosingReasonErrorCodes.NO_ERROR),
  DISCONNECTION_REQUESTED_CODE,
  END_SESSION_BY_USER_CODE,
]);

const endedWithoutError = ({ type, code }: DisconnectReason): boolean =>
  type === 'ClosingReason' && CLOSING_REASONS_WITHOUT_ERROR.has(code);

// The search params wsp.html sends the same end with, so the Disconnect page
// shows the same alert and records the same metric.
const disconnectPageFor = (reason: DisconnectReason): string =>
  endedWithoutError(reason)
    ? '/disconnect'
    : `/disconnect?${WebInSessionError.DISCONNECT_ERROR_TYPE_SEARCH_NAME}=${reason.type}&${WebInSessionError.DISCONNECT_STATUS_CODE_SEARCH_NAME}=${reason.code}`;

// The docking shell needs a fixed box that page reflow cannot disturb. Four
// edges, not 100vh: a phone's 100vh runs behind a visible browser bar.
const containerStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  zIndex: 1000,
};

const recordSessionLostToHeartbeat = (): void => {
  metrics.emit(
    AGENTIC_SAML_HEARTBEAT_OPERATION,
    SAML_HEARTBEAT_ERROR_METRIC,
    1
  );
};

const endSessionAfterHeartbeatError = (): void => {
  removeWspContextHandoff();
  recordSessionLostToHeartbeat();
};

const handleAgenticHeartbeatError = (
  error: unknown,
  controls: HeartbeatErrorControls
): void => {
  if (handleHeartbeatError(error, controls) === 'disconnecting') {
    endSessionAfterHeartbeatError();
  }
};

// With no wsp_context handoff, or on a standard WorkSpace, there is no agent.
const AgenticRoute: React.FC = () => {
  // Persisted in sessionStorage, so it survives a reload of /agentic.
  const resourceId = useSessionStore((state) => state.resourceId);
  // Routing only. The WorkSpace host enforces access.
  const resourceDetails = useSessionStore(
    (state) => state.resource?.ResourceDetails
  );
  const routeShowsViewer =
    hasWspContextHandoff() && isAgenticWorkspace(resourceDetails);
  const isLogUploaderEnabledByUser = useLogUploaderPreferenceStore(
    (state) => state.isLoggingEnabledByUser
  );
  const isLogUploaderEnabledAtDirectoryLevel = useSessionStore(
    (state) => state.isLogUploaderEnabledAtDirectoryLevel
  );
  const useDualStackIpv6 = useConnectionSettingsStore(
    (state) => state.useDualStackIPv6
  );
  // The viewer binds it once, at its first render, as Maxibon's viewer does.
  const [dcvObservability] = useState(() =>
    routeShowsViewer
      ? buildAgenticDcvObservability({
          isLogUploaderEnabledByUser,
          isLogUploaderEnabledAtDirectoryLevel,
          useDualStackIpv6,
        })
      : undefined
  );
  const navigate = useNavigate();

  const leaveAfterViewerDisconnect = useCallback(
    (reason: DisconnectReason) => {
      logger.info(
        `Agentic viewer disconnected (type: ${reason.type}, code: ${reason.code})`
      );
      removeWspContextHandoff();
      navigate(disconnectPageFor(reason));
    },
    [navigate]
  );

  const { startReportingHeartbeat } = useReportHeartbeatTask({
    onError: handleAgenticHeartbeatError,
  });

  // Maxibon routes here before it starts the heartbeat. Without one, the broker
  // ends a SAML or IAM Identity Center session about 25 minutes after sign-in.
  useEffect(() => {
    if (routeShowsViewer) {
      startReportingHeartbeat();
    }
  }, [routeShowsViewer]);

  // Unlike Maxibon, no safe-area-insets class: the viewer pads for the insets.
  useEffect(() => {
    if (!routeShowsViewer) {
      return;
    }
    // A page restored from the back/forward cache holds a dead DCV connection.
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        logger.info(
          'Page rendered from b/f cache, forcing reload to establish connection'
        );
        window.location.reload();
      }
    };
    window.addEventListener('pageshow', handlePageShow);

    const viewportMeta = document.querySelector<HTMLMetaElement>(
      'meta[name="viewport"]'
    );
    const previousViewport = viewportMeta?.getAttribute('content');
    viewportMeta?.setAttribute('content', AGENTIC_VIEWPORT);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
      if (viewportMeta && previousViewport != null) {
        viewportMeta.setAttribute('content', previousViewport);
      }
    };
  }, [routeShowsViewer]);

  if (!routeShowsViewer) {
    return <Navigate to="/" replace />;
  }

  return (
    <div style={containerStyle}>
      <AgenticRouteErrorBoundary metrics={metrics} logger={logger}>
        <Suspense fallback={<ConnectStatus />}>
          <LazyAgenticViewer
            workspaceId={resourceId || undefined}
            logger={logger}
            dcvBaseUrl={DCV_ASSETS_PATH}
            metrics={metrics}
            dcvObservability={dcvObservability}
            onDisconnect={leaveAfterViewerDisconnect}
          />
        </Suspense>
      </AgenticRouteErrorBoundary>
    </div>
  );
};

export default AgenticRoute;
